import {
  Credit,
  DefaultProxy,
  UrlTemplateImageryProvider,
  WebMercatorTilingScheme,
} from "@cesium/engine";
import defined from "../../Core/defined.js";
import DeveloperError from "../../Core/DeveloperError.js";
import Resource from "../../Core/Resource.js";
import {
  computeDegrees,
  degreesScratch,
} from "../../Scene/UrlTemplateImageryProvider.js";

/**
 * 네이버 지도 이미지 제공 클래스.</br>
 * Provides naver static imagery by requesting tiles.
 *
 * @alias NaverStaticImageryProvider
 * @constructor
 *
 * @param {object} options 다음과 같은 속성을 가진 객체.</br> Object with the following properties:
 * @param {string} options.clientId 네이버에서 발급한 클라이언트 ID.
 * </br>(API 키 발급처: https://www.ncloud.com/product/applicationService/maps)
 * </br>The clientId issued by Naver.
 * @param {string} options.clientSecret 네이버에서 발급한 클라이언트 secret key.
 * </br>(API 키 발급처: https://www.ncloud.com/product/applicationService/maps)
 * </br>The clientSecret issued by Naver.
 * @param {string} options.mapType 네이버 지도 유형.
  </br>&nbsp&nbsp&nbsp1. "basic"- 일반지도
  </br>&nbsp&nbsp&nbsp2. "traffic"- 교통지도
  </br>&nbsp&nbsp&nbsp3. "satellite"-위성지도
  </br>&nbsp&nbsp&nbsp4. "satellite_base"- 위성 배경지도
  </br>&nbsp&nbsp&nbsp5. "terrain"- 지형도
 * </br>The type of Naver map. (ex: "basic", "traffic", "satellite", "satellite_base", "terrain")
 * @param {string} options.proxy 프록시 요청 주소. 지정되지 않은 경우(기본값:"/proxy/")</br>The proxy request address is required.
 *                otherwise, Naver map will not be requested (default value: "/proxy/")
 * 
 * @example
 * 
 * 
  // DitapViewer 생성
  const viewer = new Ditap.DitapViewer("ditapContainer", {
    baseLayerPicker: true,
  });

  // DitapBaseLayerPicker 인스턴스 생성
  const ditapBaseLayerPicker = viewer.ditapBaseLayerPicker;

  const naverImageryProvider = new Ditap.ProviderViewModel({
    name: "Naver Map",
    iconUrl: getUrlFromBase64(
      Ditap.ConstantUrl.NAVER_MAP_IMG_URL
    ),
    tooltip: "Naver Map",
    category: "Other",
    creationFunction: function () {
      return new Ditap.NaverStaticImageryProvider({
        // 발급 받은 clientId 추가 (필수)
        clientId: clientId,

        // 발급 받은 clientSecret 추가 (필수)
        clientSecret: clientSecret,

        // 맵 추가 (필수)
        mapType: "basic",

        // 프록시 요청 주소 추가 (필수)
        proxy: "/proxy/",
      });
    },
  });

  ditapBaseLayerPicker.viewModel.selectedImagery = naverImageryProvider;
 */

class NaverStaticImageryProvider extends UrlTemplateImageryProvider {
  constructor(options) {
    if (!defined(options.clientId)) {
      throw new DeveloperError("options.clientId is required.");
    }
    if (!defined(options.clientSecret)) {
      throw new DeveloperError("options.clientSecret is required.");
    }
    if (!defined(options.mapType) || !isAvaillableMapType(options.mapType)) {
      throw new DeveloperError("options.mapType is required.");
    } else {
      options.mapType = getMapType(options.mapType);
    }
    if (!defined(options.proxy)) {
      throw new DeveloperError("options.proxyUrl is required.");
    } else {
      options.proxy = getProxyUrl(options.proxy);
    }

    function getProxyUrl(proxyUrl) {
      let availableProxyUrl = proxyUrl;
      if (String(proxyUrl).charAt(0) !== "/") {
        availableProxyUrl = `/${availableProxyUrl}`;
      }
      if (String(proxyUrl).charAt(proxyUrl.length - 1) !== "/") {
        availableProxyUrl = `${availableProxyUrl}/`;
      }

      return availableProxyUrl;
    }

    function getMapType(mapType) {
      mapType = String(mapType).trim().toLowerCase();
      let availableMapType;
      if (mapType === "basic") {
        availableMapType = "basic";
      } else if (mapType === "traffic") {
        availableMapType = "traffic";
      } else if (mapType === "satellite") {
        availableMapType = "satellite";
      } else if (mapType === "satellite_base") {
        availableMapType = "satellite_base";
      } else if (mapType === "terrain") {
        availableMapType = "terrain";
      }
      return availableMapType;
    }

    function isAvaillableMapType(mapType) {
      mapType = String(mapType).trim().toLowerCase();
      let isAvailableMapType = false;
      if (
        mapType === "basic" ||
        mapType === "traffic" ||
        mapType === "satellite" ||
        mapType === "satellite_base" ||
        mapType === "terrain"
      ) {
        isAvailableMapType = true;
      }
      return isAvailableMapType;
    }

    const clientId = options.clientId;
    const clientSecret = options.clientSecret;
    const customResource = new Resource({
      // proxyUrl(프록시 컨트롤러 주소) 확인
      proxy: new DefaultProxy(options.proxy),
      url: `https://naveropenapi.apigw.ntruss.com/map-static/v2/raster?w={width}&h={height}&center={centerLon},{centerLat}&level={naverZ}&maptype=${options.mapType}`,
      headers: {
        "X-NCP-APIGW-API-KEY-ID": clientId,
        "X-NCP-APIGW-API-KEY": clientSecret,
      },
    });

    const customTags = {
      centerLon: centerLonTag,
      centerLat: centerLatTag,
      naverZ: naverZTag,
    };

    function centerLonTag(imageryProvider, x, y, level) {
      computeDegrees(imageryProvider, x, y, level);
      return (degreesScratch.west + degreesScratch.east) / 2;
    }

    function centerLatTag(imageryProvider, x, y, level) {
      computeDegrees(imageryProvider, x, y, level);
      return (degreesScratch.north + degreesScratch.south) / 2;
    }

    function naverZTag(imageryProvider, x, y, level) {
      computeDegrees(imageryProvider, x, y, level);
      return level - 1;
    }

    const resource = customResource;
    options.url = resource;
    options.tilingScheme = new WebMercatorTilingScheme();
    options.tileWidth = 256;
    options.tileHeight = 256;
    options.minimumLevel = 0;
    options.rectangle = options.tilingScheme.rectangle;
    options.hasAlphaChannel = true;
    options.customTags = customTags;
    options.credit = new Credit("© NAVER Corp, CC-BY-ND");

    super(options);
  }

  getTileCredits() {
    return;
  }

  pickFeatures() {
    return;
  }

  // requestImage() {
  //   return;
  // }
}
export default NaverStaticImageryProvider;
